import React, { useState, useEffect, useContext, useRef } from "react";
import styled from "styled-components";
import Select from "../../../theme/CustomMUI/Select";
import AppContext from "../../../context/store";
import axios from "axios";
import StaffImg from "./StaffImg";
import AdBlockChecker from "./AdBlockChecker";
import iframeResize from 'iframe-resizer/js/iframeResizer';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 500;
  color: #111827;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 350px;
  position: relative;

  .MuiInputBase-root {
    width: 100%;
  }

  > div {
    width: 100%;
  }
`;

const Content = styled.div`
  flex: 1;
  width: 100%;
  height: calc(100vh - 120px);
  margin-top: 20px;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 2px solid #e5e7eb;
  border-bottom-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  text-align: center;
  padding: 32px;
  background-color: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  margin: 16px;

  p {
    margin: 8px 0;
  }
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  min-height: 100vh;
`;

// Çalışan Dashboard bileşeni
const StaffDashboard = () => {
  const context = useContext(AppContext);
  const [iframeUrl, setIframeUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffs, setStaffs] = useState([]);
  const [dashboardId, setDashboardId] = useState("11");
  const [state, setState] = useState({
    user: null
  });

  // URL önbelleği için ref kullanımı
  const urlCache = useRef({});

  // Personel listesini yükle
  useEffect(() => {
    // Get user info from context
    if (context.user) {
      setState(prev => ({ ...prev, user: context.user }));
    }

    // Get staff list
    const getStaffs = async () => {
      try {
        const response = await axios.get(`${context.api_endpoint}/company/staffs/unsafe`);
        if (response.data && response.data.data) {
          setStaffs(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching staff list:", error);
      }
    };

    getStaffs();
  }, [context]);

  // Seçilen personel değiştiğinde veya sayfa yüklendiğinde API isteği yap
  useEffect(() => {
    const fetchEmbedUrl = async () => {
      try {
        setLoading(true);

        // Uygulama adı ve test modu bilgisi al
        const appName = process.env.APP_NAME;
        const isTest = process.env.IS_TEST;

        // Seçilen personel varsa personel dashboard'unu, yoksa tüm personeller dashboard'unu göster
        const dashboardData = isTest
          ? selectedStaff
            ? process.env.METABASE_IDS.TEST.STAFF
            : process.env.METABASE_IDS.TEST.STAFFS
          : selectedStaff
            ? process.env.METABASE_IDS.LIVE.STAFF[appName === "salon" ? "SR" : appName === "en" ? "ER" : "SM"]
            : process.env.METABASE_IDS.LIVE.STAFFS[appName === "salon" ? "SR" : appName === "en" ? "ER" : "SM"];

        const dashboardId = dashboardData.id.toString();
        setDashboardId(dashboardId);

        // Önbellek kontrolü
        let cachedUrl = null;

        if (!selectedStaff) {
          // Tüm personeller raporu için önbellek kontrolü
          cachedUrl = urlCache.current[`dashboard${dashboardId}`];
        } else {
          // Personel dashboard'u için önbellek kontrolü
          if (!urlCache.current[`dashboard${dashboardId}`]) {
            urlCache.current[`dashboard${dashboardId}`] = {};
          }
          cachedUrl = urlCache.current[`dashboard${dashboardId}`][selectedStaff.id];
        }

        // Eğer önbellekte URL varsa, API isteği atma
        if (cachedUrl) {
          console.log("Önbellekten URL kullanılıyor:", dashboardId);
          setIframeUrl(cachedUrl);
          setLoading(false);
          return;
        }

        // Önbellekte yoksa API isteği at
        const endpoint = `company/analytics/embed/dashboard/${dashboardId}`;

        // Eğer personel seçildiyse staff_id parametresini ekle
        const params = new URLSearchParams();
        if (selectedStaff && dashboardData.filters && dashboardData.filters.staff_id) {
          params.append("staff_id", selectedStaff.id);
        }

        const response = await axios.get(`${context.api_endpoint}/${endpoint}${params.toString() ? '?' + params.toString() : ''}`);

        if (response.data && response.data.data) {
          const embedUrl = response.data.data;
          setIframeUrl(embedUrl);

          // Önbelleğe kaydet
          if (!selectedStaff) {
            urlCache.current[`dashboard${dashboardId}`] = embedUrl;
          } else {
            urlCache.current[`dashboard${dashboardId}`][selectedStaff.id] = embedUrl;
          }

          // iframe yüklendikten sonra resize işlemini başlat
          setTimeout(() => {
            iframeResize({
              log: false,
              checkOrigin: false,
              heightCalculationMethod: 'max'
            }, 'iframe');
          }, 1000);
        } else {
          setError("Rapor URL'si alınamadı");
        }
      } catch (err) {
        setError("Rapor yüklenirken bir hata oluştu");
        console.error("Rapor yükleme hatası:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmbedUrl();
  }, [selectedStaff, context.api_endpoint]);

  // Önbelleği temizleme fonksiyonu - ihtiyaç halinde kullanılabilir
  const clearCache = () => {
    urlCache.current = {};
  };

  // Personel seçildiğinde tetiklenir
  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
  };

  return (
    <Container>
      <TopBar>
        <Title>
          {selectedStaff && <StaffImg staff_id={selectedStaff.id} />}
        </Title>
        <SelectContainer>
          <SelectWrapper>
            <Select
              label={context.t(`appointments.create.selectedStaffInputLabel`)}
              items={[
                { id: null, full_name: context.t("todos.noStaff") },
                ...staffs.filter(staff => state.user ? staff.id !== state.user.id : true)
              ]}
              labelKey="full_name"
              valueKey="id"
              selected={selectedStaff ? `${selectedStaff.id}` : ""}
              returnFullObject
              handler={handleStaffSelect}
              style={{ width: "100%", borderRadius: "50px" }}
            />
          </SelectWrapper>
        </SelectContainer>
      </TopBar>

      <Content>
        {loading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : error ? (
          <ErrorMessage>
            <p><strong>{context.t("newReports.error.title")}</strong></p>
            <p>{context.t("newReports.error.description")}</p>
            {context.t("newReports.error.steps").map((step, index) => (
              <p key={index}>{step}</p>
            ))}
          </ErrorMessage>
        ) : (
          <>
            <AdBlockChecker />
            <StyledIframe
              id="iframe"
              src={iframeUrl}
              allowTransparency
            />
          </>
        )}
      </Content>
    </Container>
  );
};

export default StaffDashboard;
